import { PageHeader } from '@ant-design/pro-layout'
import { Editor } from '@monaco-editor/react'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { canUseLlmOptimizeSql, useMe } from '@salescore/client-common'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  CORE_CONSTANT,
  type ViewQueryList,
} from '@salescore/core'
import { CodeHighlighter, CopyButton } from '@salescore/frontend-common'
import type { ModalFuncProps } from 'antd'
import { Button, Empty, Flex, Form, message, Space, Typography } from 'antd'
import Search from 'antd/es/input/Search'
import { t } from 'i18next'
import { type ReactElement, useMemo, useState } from 'react'
import { format } from 'sql-formatter'

import { type SQLItem, useSqlItems, useSqlOperations, useSqlSearch } from './ViewQueryResultsMetadataModal/hooks'

// SQL編集を許可しないビューのプレフィックス
const EXCLUDED_VIEW_PREFIXES = [
  CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX,
  CORE_CONSTANT.KPI_PREVIEW_VIEW_ID_PREFIX,
  CORE_CONSTANT.KPI_PREVIEW_DRILL_DOWN_VIEW_ID_PREFIX,
  CORE_CONSTANT.SHEET_FORM_DYNAMIC_VIEW_ID_PREFIX,
] as const

export const buildViewQueryResultsMetadataModalProperties = (
  // eslint-disable-next-line sonarjs/use-type-alias
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
  query: ViewQueryList | undefined,
): ModalFuncProps => ({
  icon: null,
  title: <PageHeader title={`SQL${t(`実行状況詳細`)}`} />,
  width: '60%',
  cancelText: t(`閉じる`),
  okButtonProps: { style: { display: 'none' } },
  style: { top: '3%' },
  closable: true,
  maskClosable: true,
  content: <ViewQueryResultsMetadataModalBody compiledConfig={compiledConfig} view={view} query={query} />,
})

export const ViewQueryResultsMetadataModalBody = ({
  compiledConfig,
  view,
  query,
}: {
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList | undefined
}): ReactElement => {
  const me = useMe()
  const { sqlItems, errors, isEmptyQueryResults } = useSqlItems(compiledConfig, view)
  const { filteredSqlItems, searchSql } = useSqlSearch(sqlItems)
  const { optimizeAllSqls, isOptimizeSqlsWithLlmLoading } = useSqlOperations(compiledConfig, view, query)

  // ドリルダウンやプレビュー、シートフォームの場合はSQLの編集を許可しない
  const isRootView = useMemo(() => EXCLUDED_VIEW_PREFIXES.every((prefix) => !view.id.startsWith(prefix)), [view.id])
  const isSqlEditable = useMemo(() => canUseLlmOptimizeSql(me) && isRootView, [me, isRootView])

  if (isEmptyQueryResults) {
    return <></>
  }

  return (
    <>
      {errors.length > 0 && (
        <div className="mb-8">
          <Typography.Title level={4}>{t(`エラーが発生した`)}SQL</Typography.Title>
          {errors.map((error) => (
            <div key={error} className="text-red-500">
              {error}
            </div>
          ))}
        </div>
      )}
      <div className="mb-8">
        <Flex className="mb-4" vertical align="start" gap={16}>
          <h4 className="mb-0 text-xl font-semibold">SQL{t(`一覧`)}</h4>
          <Flex className="w-full" align="center" gap={8}>
            <Search
              placeholder={t(`SQL名を検索`)}
              allowClear
              onSearch={(keyword) => {
                searchSql(keyword)
              }}
            />
            {isSqlEditable && (
              <Button
                type="primary"
                loading={isOptimizeSqlsWithLlmLoading}
                disabled={isOptimizeSqlsWithLlmLoading}
                onClick={() => {
                  void optimizeAllSqls({
                    sqlItems,
                    onCompleted: (completeMessage: string) => {
                      message.success(completeMessage)
                    },
                    onError: (error) => {
                      message.error(error.message)
                    },
                  })
                }}
              >
                {t(`LLMで一括最適化`)}
              </Button>
            )}
          </Flex>
        </Flex>
        {filteredSqlItems.length > 0 ? (
          filteredSqlItems.map((item) => (
            <SqlDetail
              sql={item}
              key={item.title}
              isEditable={isSqlEditable}
              isParentLoading={isOptimizeSqlsWithLlmLoading}
              compiledConfig={compiledConfig}
              view={view}
              query={query}
            />
          ))
        ) : (
          <Empty description={t(`SQLが見つかりません`)} />
        )}
      </div>
    </>
  )
}

export const SqlDetail = ({
  sql,
  isParentLoading,
  isEditable,
  compiledConfig,
  view,
  query,
}: {
  sql: SQLItem
  isParentLoading: boolean
  isEditable: boolean
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList | undefined
}): ReactElement => {
  const {
    updateSql,
    optimizeSql,
    resetSql,
    isUpsertViewCustomSqlLoading,
    isOptimizeSqlsWithLlmLoading,
    isDeleteViewCustomSqlLoading,
  } = useSqlOperations(compiledConfig, view, query)
  const [isEditing, setIsEditing] = useState(false)
  const isLoading =
    isUpsertViewCustomSqlLoading || isOptimizeSqlsWithLlmLoading || isDeleteViewCustomSqlLoading || isParentLoading

  return (
    <div className="mb-8">
      <Typography.Title level={5}>
        <Space>
          {sql.title}
          <CopyButton text={sql.displaySql} />
          {isEditable && (
            <>
              <Button
                disabled={isEditing || isLoading}
                onClick={() => {
                  setIsEditing(true)
                }}
              >
                {t(`SQLを編集`)}
              </Button>
              <Button
                color="primary"
                variant="outlined"
                disabled={isEditing || isLoading}
                loading={isLoading}
                onClick={() => {
                  void optimizeSql({
                    originalRawSql: sql.originalRawSql,
                    mustacheParameter: sql.mustacheParameter,
                    onCompleted: () => {
                      message.success(t(`SQLを最適化しました`))
                    },
                    onError: (error) => {
                      message.error(error.message)
                    },
                  })
                }}
              >
                {t(`LLMで最適化`)}
              </Button>
              {sql.overriddenRawSql !== undefined && (
                <Button
                  color="danger"
                  variant="dashed"
                  disabled={isEditing || isLoading}
                  loading={isLoading}
                  onClick={() => {
                    void resetSql({
                      originalRawSql: sql.originalRawSql,
                      onCompleted: () => {
                        message.success(t(`上書きSQLをリセットしました`))
                      },
                      onError: (error) => {
                        message.error(error.message)
                      },
                    })
                  }}
                >
                  {t(`上書きSQLをリセット`)}
                </Button>
              )}
            </>
          )}
        </Space>
      </Typography.Title>
      {isEditing ? (
        <SqlEditor
          formValue={{ sql: sql.overriddenRawSql ?? sql.originalRawSql }}
          disabled={isLoading}
          loading={isLoading}
          onFinish={(value) => {
            void updateSql({
              customRawSql: value.sql,
              originalRawSql: sql.originalRawSql,
              mustacheParameter: sql.mustacheParameter,
              onCompleted: () => {
                message.success(t(`SQLを更新しました`))
                setIsEditing(false)
              },
              onError: (error) => {
                message.error(error.message)
              },
            })
          }}
          onCancel={() => {
            setIsEditing(false)
          }}
        />
      ) : (
        <CodeHighlighter language="sql">
          {format(sql.displaySql, {
            language: 'postgresql',
          })}
        </CodeHighlighter>
      )}
    </div>
  )
}

export interface SqlEditFormValue {
  sql: string
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function SqlEditor({
  formValue,
  loading,
  disabled,
  onFinish,
  onCancel,
}: {
  formValue: SqlEditFormValue
  loading: boolean
  disabled: boolean
  onFinish: (value: SqlEditFormValue) => void
  onCancel: () => void
}): React.ReactNode {
  return (
    <Form
      onFinish={onFinish}
      initialValues={{
        sql: format(formValue.sql, {
          language: 'postgresql',
        }),
      }}
    >
      <Form.Item name="sql">
        <Editor height={600} theme="vs-dark" defaultLanguage="sql" />
      </Form.Item>
      <Form.Item>
        <Flex justify="end" gap={8}>
          <Button htmlType="button" onClick={onCancel} disabled={loading}>
            {t(`キャンセル`)}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading} disabled={loading || disabled}>
            {t(`更新`)}
          </Button>
        </Flex>
      </Form.Item>
    </Form>
  )
}
